// Proposal.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Services from "../service/Services";

// ── Sanitize pasted HTML from Microsoft Word / DOCX ─────────────────
// Preserves paragraphs, line breaks, bullets, numbered lists, nested lists,
// bold / italic / underline, and basic text alignment.
function cleanWordHtml(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, "text/html");

    // Strip Word/Office noise
    doc.querySelectorAll("style, script, meta, link, xml, o\\:p").forEach((el) => el.remove());

    const allowedTags = new Set([
      "P", "BR", "DIV", "SPAN",
      "UL", "OL", "LI",
      "B", "STRONG", "I", "EM", "U",
      "H1", "H2", "H3", "H4", "H5", "H6",
      "SUB", "SUP"
    ]);
    const allowedStyles = new Set([
      "text-align", "font-weight", "font-style", "text-decoration",
      "font-size", "color"
    ]);

    function cleanNode(node) {
      if (node.nodeType === Node.TEXT_NODE) {
        return document.createTextNode(node.textContent);
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return null;

      const tag = node.tagName.toUpperCase();
      if (!allowedTags.has(tag)) {
        // Unwrap: keep children
        const frag = document.createDocumentFragment();
        Array.from(node.childNodes).forEach((child) => {
          const cleaned = cleanNode(child);
          if (cleaned) frag.appendChild(cleaned);
        });
        return frag.childNodes.length ? frag : null;
      }

      const el = document.createElement(tag.toLowerCase());

      // Preserve useful inline styles
      if (node.style) {
        allowedStyles.forEach((prop) => {
          const val = node.style.getPropertyValue(prop);
          if (val) el.style.setProperty(prop, val);
        });
      }
      // Legacy align attribute
      const align = node.getAttribute("align");
      if (align) el.style.textAlign = align;

      Array.from(node.childNodes).forEach((child) => {
        const cleaned = cleanNode(child);
        if (cleaned) el.appendChild(cleaned);
      });
      return el;
    }

    const wrapper = document.createElement("div");
    Array.from(doc.body.childNodes).forEach((child) => {
      const cleaned = cleanNode(child);
      if (cleaned) wrapper.appendChild(cleaned);
    });

    // Normalize empty paragraphs that Word often inserts
    let html = wrapper.innerHTML
      .replace(/<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "")
      .replace(/(<br\s*\/?>\s*){3,}/gi, "<br><br>");
    return html;
  } catch (err) {
    console.warn("HTML clean failed, falling back to plain text", err);
    return dirty.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}

// ── Rich-text editor that preserves Word/DOCX paste formatting ──────
const RichTextEditor = ({ value = "", onChange, rows = 3, placeholder = "" }) => {
  const ref = useRef(null);
  const focused = useRef(false);

  // Sync external value only when not focused (avoids cursor jumps)
  useEffect(() => {
    if (ref.current && !focused.current) {
      const current = ref.current.innerHTML;
      const next = value || "";
      if (current !== next) {
        ref.current.innerHTML = next;
      }
    }
  }, [value]);

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");
    let insert = "";
    if (html && html.trim()) {
      insert = cleanWordHtml(html);
    } else if (plain) {
      // Convert plain text line breaks into paragraphs / <br>
      insert = plain
        .split(/\n{2,}/)
        .map((para) => {
          const withBreaks = para.replace(/\n/g, "<br>");
          return `<p>${withBreaks}</p>`;
        })
        .join("");
    }
    if (insert) {
      // execCommand still works reliably inside contentEditable
      document.execCommand("insertHTML", false, insert);
    }
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      className="rich-editor"
      data-placeholder={placeholder}
      style={{ minHeight: `${Math.max(rows, 2) * 1.6}em` }}
      onFocus={() => { focused.current = true; }}
      onBlur={(e) => {
        focused.current = false;
        if (typeof onChange === "function") {
          onChange(e.currentTarget.innerHTML);
        }
      }}
      onPaste={handlePaste}
    />
  );
};

const Proposal = () => {
  const pdfRef = useRef();
  const historyPdfRef = useRef();
  const editorSectionRef = useRef(null);

  // ── History state ─────────────────────────────────────────────────
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyPreview, setHistoryPreview] = useState({
    open: false,
    id: "",
    clientName: "",
    proposal: null,
  });
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [savingHistory, setSavingHistory] = useState(false);

  // ── Preview page images (exact match to downloaded PDF) ───────────
  const [previewPages, setPreviewPages] = useState([]);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [historyPreviewPages, setHistoryPreviewPages] = useState([]);
  const [generatingHistoryPreview, setGeneratingHistoryPreview] = useState(false);

  // ── Client Name ─────────────────────────────────────────────────
  const [clientName, setClientName] = useState("CRM DEVELOPMENT- PRESENTATION PEOPLE");

  // ── Introduction Blocks ─────────────────────────────────────────
  const [introBlocks, setIntroBlocks] = useState([
    {
      id: 1,
      heading: "Introduction",
      active: true,
      paragraphs: [
        "Established in 2015, Zaptas Technologies is a leading IT company focused on RPA (Robotic Process Automation), ERP support (SAP), HR Consulting/Contract staffing, Mobile and web development. We believe in providing \"Innovative, Intelligent and Integrated\" solutions for our customers.",
        "Our team of professionals has cumulative experience of years engaged in conceptualizing, designing and implementing end-to-end IT solutions for business across industries. We help our customers to produce more with less, better and faster, while simultaneously and continuously finding ways to add value to their business environment."
      ]
    }
  ]);

  // ── Services Blocks ─────────────────────────────────────────────
  const [servicesBlocks, setServicesBlocks] = useState([
    {
      id: 1,
      heading: "Our Services",
      active: true,
      intro: "Zaptas Technologies provides expert services in the following domains :-",
      items: [
        "RPA (Robotic Process Automation)",
        "SAP Support",
        "HR Consulting (IT Staffing)",
        "Web Design and Development",
        "Mobile App Development (Android & iOS)"
      ]
    }
  ]);

  // ── Development Blocks ──────────────────────────────────────────
  const [developmentBlocks, setDevelopmentBlocks] = useState([
    {
      id: 1,
      heading: "Software Development",
      active: true,
      intro: "Custom software development means development of software to meet business specific requirement of client. Custom software is exactly what client requires for its business environment and is developed in following steps :-",
      steps: [
        "Understanding business process ",
        "Requirement Gathering",
        "Designing database ",
        "Write clean, reusable and scalable code",
        "Testing and debugging",
        "Release BETA version",
        "Incorporate inclusions, if any",
        "Go Live"
      ],
      copyPasteText: "",
      sectionImage: null,
      sectionImageName: "",
      sectionImageWidth: 100,
      sectionImageHeight: 0   // 0 = auto; positive number = fixed px height
    }
  ]);

  // ── Customer Background Blocks ──────────────────────────────────
  const [customerBackgroundBlocks, setCustomerBackgroundBlocks] = useState([
    {
      id: 1,
      heading: "Customer Background",
      active: true,
      paragraphs: [
        "The client is a leading organization in their domain, seeking to enhance operational efficiency through modern technology solutions. They aim to streamline internal processes, improve data management, and create more seamless workflows across departments. By adopting scalable and user-friendly digital systems, the organization intends to reduce manual effort, minimize errors, and enable faster decision-making.",
        "With a strong focus on digital transformation, they aim to streamline business processes, reduce manual dependency, and improve overall operational efficiency across departments. By implementing modern, scalable technologies and leveraging data-driven insights, the organization seeks to accelerate decision-making, enhance service delivery, and ensure greater accuracy and transparency. Their strategy also prioritizes improving customer engagement through faster response times, personalized interactions, and seamless digital touchpoints. Ultimately, this transformation is designed to strengthen long-term growth, adaptability, and customer satisfaction in an increasingly competitive and technology-driven environment."
      ]
    }
  ]);

  // ── Scope Blocks ────────────────────────────────────────────────
  const [scopeBlocks, setScopeBlocks] = useState([
    {
      id: 1,
      heading: "Scope of Work",
      active: true,
      intro: "The objective of this project is to design and develop a web-based software solution. The initial phase shall consist of the following functional modules :-",
      sections: [
        // {
        //   title: "1. System Admin (Dashboard)",
        //   items: [
        //     "User & role management",
        //     "Dashboard with key metrics",
        //     "Audit logs & activity tracking"
        //   ]
        // },
        // {
        //   title: "2. Lead & Opportunity Management",
        //   items: [
        //     "Lead capture & qualification",
        //     "Opportunity pipeline tracking",
        //     "Automated email notifications"
        //   ]
        // }
      ]
    }
  ]);

  // ── Timeline Blocks ─────────────────────────────────────────────
  const [timelineBlocks, setTimelineBlocks] = useState([
    {
      id: 1,
      heading: "Timeline",
      active: true,
      intro: "The project is estimated to be completed within",
      months: "3 months",
      postfix: ", assuming timely provision of feedback, assets, and approvals from the client side."
    }
  ]);

  // ── Costing Blocks ──────────────────────────────────────────────
  const [costingBlocks, setCostingBlocks] = useState([
    {
      id: 1,
      heading: "Costing",
      active: true,
      mainItems: [
        { desc: "SAP/Salesforce Integration Consultant", amt: "1000.00" },
        { desc: "(@ /-pm × Months)", amt: "0.00" }
      ],
      extraItems: [
        { desc: "GST @ 18%", amt: "180.00" }
      ]
    }
  ]);

  // ── Terms Blocks ────────────────────────────────────────────────
  const [termsBlocks, setTermsBlocks] = useState([
    {
      id: 1,
      heading: "Terms & Conditions",
      active: true,
      items: [
        "The resource shall work on hybrid model. Mainly remote but will visit client site as and when required for project need.",
        "Work shall be done on time basis to meet the project deadlines.",
        "The accommodation, travel expenses shall be borne by client for the visits made by consultant.",
        "Zaptas shall raise invoice on monthly basis that needs to be paid by client within 7 days of receipt."
      ]
    }
  ]);

  // ── Accordion open state (per block id) ─────────────────────────
  const [openAccordions, setOpenAccordions] = useState({});

  const toggleAccordion = (sectionType, blockId) => {
    setOpenAccordions(prev => ({
      ...prev,
      [`${sectionType}-${blockId}`]: !prev[`${sectionType}-${blockId}`]
    }));
  };

  const getProposalSnapshot = (overrides = {}) => ({
    clientName: overrides.clientName ?? clientName,
    introBlocks,
    servicesBlocks,
    developmentBlocks,
    customerBackgroundBlocks,
    scopeBlocks,
    timelineBlocks,
    costingBlocks,
    termsBlocks,
  });

  const applyProposalSnapshot = (snapshot) => {
    if (!snapshot || typeof snapshot !== "object") return false;
    const data = JSON.parse(JSON.stringify(snapshot));
    if (data.clientName != null) setClientName(data.clientName);
    if (Array.isArray(data.introBlocks)) setIntroBlocks(data.introBlocks);
    if (Array.isArray(data.servicesBlocks)) setServicesBlocks(data.servicesBlocks);
    if (Array.isArray(data.developmentBlocks)) setDevelopmentBlocks(data.developmentBlocks);
    if (Array.isArray(data.customerBackgroundBlocks)) setCustomerBackgroundBlocks(data.customerBackgroundBlocks);
    if (Array.isArray(data.scopeBlocks)) setScopeBlocks(data.scopeBlocks);
    if (Array.isArray(data.timelineBlocks)) setTimelineBlocks(data.timelineBlocks);
    if (Array.isArray(data.costingBlocks)) setCostingBlocks(data.costingBlocks);
    if (Array.isArray(data.termsBlocks)) setTermsBlocks(data.termsBlocks);
    return true;
  };

  // ── Helpers ─────────────────────────────────────────────────────
  const duplicateSection = (blocks, setBlocks, index, defaultHeadingSuffix = " (Copy)") => {
    const newBlock = {
      ...blocks[index],
      id: Date.now(),
      heading: blocks[index].heading + defaultHeadingSuffix,
      active: true
    };
    const newArray = [...blocks];
    newArray.splice(index + 1, 0, newBlock);
    setBlocks(newArray);
  };

  const removeSection = (blocks, setBlocks, index) => {
    if (blocks.length <= 1) return;
    const newArray = blocks.filter((_, i) => i !== index);
    setBlocks(newArray);
  };

  const updateBlock = (blocks, setBlocks, index, updates) => {
    const newArray = [...blocks];
    newArray[index] = { ...newArray[index], ...updates };
    setBlocks(newArray);
  };

  const updateArrayField = (blocks, setBlocks, blockIndex, field, newValue) => {
    const newArray = [...blocks];
    newArray[blockIndex] = { ...newArray[blockIndex], [field]: newValue };
    setBlocks(newArray);
  };

  // Costing helpers
  const calculateSubtotal = (items) =>
    items.reduce((sum, item) => sum + (parseFloat(String(item.amt || "0").replace(/[^0-9.-]/g, '')) || 0), 0);

  const calculateExtra = (items) =>
    items.reduce((sum, item) => sum + (parseFloat(String(item.amt || "0").replace(/[^0-9.-]/g, '')) || 0), 0);

  const formatAmount = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "/-";
  };

  // ── Development image upload handler ────────────────────────────
  const handleDevImageUpload = (blockIndex, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const copy = [...developmentBlocks];
      copy[blockIndex] = {
        ...copy[blockIndex],
        sectionImage: e.target.result,
        sectionImageName: file.name
      };
      setDevelopmentBlocks(copy);
    };
    reader.readAsDataURL(file);
  };

  const removeDevImage = (blockIndex) => {
    const copy = [...developmentBlocks];
    copy[blockIndex] = { ...copy[blockIndex], sectionImage: null, sectionImageName: "" };
    setDevelopmentBlocks(copy);
  };

  // ── Shared page-slicing logic (used by both PDF download & preview) ─
  // Returns array of dataURL images, one per A4 page. Identical pagination
  // guarantees the preview matches the downloaded PDF exactly.
  const sliceCanvasToPages = (canvas) => {
    const pageWidth = 210;
    const pageHeight = 297;
    const marginTop = 15;
    const marginBottom = 15;
    const marginLeft = 10;
    const marginRight = 10;

    const printableWidth = pageWidth - marginLeft - marginRight;
    const printableHeight = pageHeight - marginTop - marginBottom;

    const imgWidth = printableWidth;
    const pxFullHeight = canvas.height;
    const pxPageHeight = (printableHeight * canvas.width) / printableWidth;

    const pages = [];
    let currentPosition = 0;

    while (currentPosition < pxFullHeight) {
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = Math.min(pxPageHeight, pxFullHeight - currentPosition);

      const ctx = pageCanvas.getContext("2d");
      // White background so transparent areas are clean
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(
        canvas,
        0, currentPosition,
        canvas.width, pageCanvas.height,
        0, 0,
        canvas.width, pageCanvas.height
      );

      pages.push(pageCanvas.toDataURL("image/png"));
      currentPosition += pxPageHeight;
    }

    return pages;
  };

  // ── Generate preview page images (exact same pipeline as PDF) ─────
  const generatePreviewPages = useCallback(async (inputEl) => {
    if (!inputEl) return [];
    // Wait a tick so images / fonts inside the hidden content are painted
    await new Promise((r) => setTimeout(r, 50));
    const canvas = await html2canvas(inputEl, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      windowWidth: inputEl.scrollWidth,
      // Ensure tall content is fully captured
      height: inputEl.scrollHeight,
      windowHeight: inputEl.scrollHeight
    });
    return sliceCanvasToPages(canvas);
  }, []);

  // ── Live preview regeneration (debounced) ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!pdfRef.current) return;
      setGeneratingPreview(true);
      try {
        const pages = await generatePreviewPages(pdfRef.current);
        if (!cancelled) setPreviewPages(pages);
      } catch (err) {
        console.error("Preview generation failed", err);
        if (!cancelled) setPreviewPages([]);
      } finally {
        if (!cancelled) setGeneratingPreview(false);
      }
    }, 450); // debounce so rapid edits don't thrash

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    clientName,
    introBlocks,
    servicesBlocks,
    developmentBlocks,
    customerBackgroundBlocks,
    scopeBlocks,
    timelineBlocks,
    costingBlocks,
    termsBlocks,
    generatePreviewPages
  ]);

  // ── History modal preview generation ──────────────────────────────
  useEffect(() => {
    if (!historyPreview.open) {
      setHistoryPreviewPages([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      if (!historyPdfRef.current) return;
      setGeneratingHistoryPreview(true);
      try {
        const pages = await generatePreviewPages(historyPdfRef.current);
        if (!cancelled) setHistoryPreviewPages(pages);
      } catch (err) {
        console.error("History preview generation failed", err);
        if (!cancelled) setHistoryPreviewPages([]);
      } finally {
        if (!cancelled) setGeneratingHistoryPreview(false);
      }
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [historyPreview.open, historyPreview.clientName, historyPreview.proposal, generatePreviewPages]);

  // ── PDF Download ────────────────────────────────────────────────
  const downloadPDF = async (opts = {}) => {
    const input = opts.ref?.current || pdfRef.current;
    const pdfClientName = opts.clientName || clientName;
    if (!input) return;

    try {
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        windowWidth: input.scrollWidth,
        height: input.scrollHeight,
        windowHeight: input.scrollHeight
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const marginTop = 15;
      const marginBottom = 15;
      const marginLeft = 10;
      const marginRight = 10;

      const printableWidth = pageWidth - marginLeft - marginRight;
      const printableHeight = pageHeight - marginTop - marginBottom;

      const imgWidth = printableWidth;
      const pxFullHeight = canvas.height;
      const pxPageHeight = (printableHeight * canvas.width) / printableWidth;

      let currentPosition = 0;

      while (currentPosition < pxFullHeight) {
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(pxPageHeight, pxFullHeight - currentPosition);

        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0, currentPosition,
          canvas.width, pageCanvas.height,
          0, 0,
          canvas.width, pageCanvas.height
        );

        const imgData = pageCanvas.toDataURL("image/png");
        const pageImgHeight = (pageCanvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", marginLeft, marginTop, imgWidth, pageImgHeight);

        currentPosition += pxPageHeight;
        if (currentPosition < pxFullHeight) pdf.addPage();
      }

      pdf.save(`Proposal_${String(pdfClientName).replace(/\s+/g, "_")}.pdf`);

      try {
        if (!opts.skipLog) {
          await Services.ProposalAndRateCard.logProposalDownload({
            clientName: pdfClientName,
            meta: { proposal: getProposalSnapshot({ clientName: pdfClientName }) },
          });
        }
        loadHistory();
      } catch (e) {
        console.error("Failed to log proposal download", e);
      }
    } catch (err) {
      console.error(err);
      alert("PDF creation failed.");
    }
  };

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      setHistoryError("");
      const res = await Services.ProposalAndRateCard.getProposalDownloads({
        limit: 20,
      });
      if (res && res.success && Array.isArray(res.data)) {
        setDownloadHistory(res.data);
      } else if (Array.isArray(res)) {
        setDownloadHistory(res);
      } else {
        setHistoryError("Could not load history");
      }
    } catch (err) {
      console.error("Failed to load proposal download history", err);
      setHistoryError("Failed to load history");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const handleEditFromHistory = (row) => {
    const proposal = row.meta?.proposal;
    if (proposal) {
      applyProposalSnapshot(proposal);
    } else {
      setClientName(row.clientName || "");
    }
    setEditingHistoryId(row._id || null);
    setOpenAccordions((prev) => ({ ...prev, "client-name": true }));
    editorSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const saveToHistory = async () => {
    const name = String(clientName || "").trim();
    if (!name) {
      alert("Client name is required.");
      return;
    }
    try {
      setSavingHistory(true);
      const proposal = getProposalSnapshot({ clientName: name });
      await Services.ProposalAndRateCard.logProposalDownload({
        clientName: name,
        meta: { proposal },
      });
      if (editingHistoryId) {
        try {
          await Services.ProposalAndRateCard.deleteProposalDownload(editingHistoryId);
        } catch (e) {
          console.error("Failed to delete previous proposal download record", e);
        }
        setEditingHistoryId(null);
      }
      loadHistory();
    } catch (e) {
      console.error("Failed to save proposal to history", e);
      alert("Save failed.");
    } finally {
      setSavingHistory(false);
    }
  };

  const downloadFromHistoryAndReplace = async () => {
    try {
      await downloadPDF({
        ref: historyPdfRef,
        clientName: historyPreview.clientName,
        skipLog: true,
      });

      await Services.ProposalAndRateCard.logProposalDownload({
        clientName: historyPreview.clientName,
        meta: {
          proposal: historyPreview.proposal || getProposalSnapshot({
            clientName: historyPreview.clientName,
          }),
        },
      });

      if (historyPreview.id) {
        try {
          await Services.ProposalAndRateCard.deleteProposalDownload(historyPreview.id);
        } catch (e) {
          console.error("Failed to delete previous proposal download record", e);
        }
      }

      loadHistory();
    } catch (e) {
      console.error("Failed to download/log proposal from history", e);
    }
  };

  // ── Shared PDF content renderer ──────────────────────────────────
  // Kept identical to original so capture + pagination stay the same.
  const renderPdfContent = (refProp, previewClientName, contentOverride = null) => {
    const intro = contentOverride?.introBlocks ?? introBlocks;
    const services = contentOverride?.servicesBlocks ?? servicesBlocks;
    const development = contentOverride?.developmentBlocks ?? developmentBlocks;
    const customerBackground = contentOverride?.customerBackgroundBlocks ?? customerBackgroundBlocks;
    const scope = contentOverride?.scopeBlocks ?? scopeBlocks;
    const timeline = contentOverride?.timelineBlocks ?? timelineBlocks;
    const costing = contentOverride?.costingBlocks ?? costingBlocks;
    const terms = contentOverride?.termsBlocks ?? termsBlocks;

    return (
    <div ref={refProp} className="a4-content">

      {/* Cover Page */}
      <div className="pdf-page">
        <div className="logo-container">
          <img src="/header.jpg" alt="Logo" className="logo1" />
        </div>
        <h1 className="pdf" style={{ textAlign: "center", marginTop: "-10mm", color: "#14415C" }}>PROPOSAL</h1>
        <h1 className="pdf" style={{ textAlign: "center", color: "#14415C" }}>FOR</h1>
        <h1 className="pdf" style={{ textAlign: "center", color: "#14415C" }}>
          {String(previewClientName || "").toUpperCase()}
        </h1>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "20mm" }}>
          <img src="./zimage12.png" alt="Client" style={{ width: "245px", height: "auto", marginTop: "10mm", marginBottom: "5mm" }} />
        </div>
        <div style={{ textAlign: "center", marginTop: "20mm" }}>
          <h4 style={{ color: "#14405C", fontSize: "27pt", fontWeight: "bold" }}>By</h4>
          <h3 style={{ marginBottom: "-15px", color: "#14405C", fontSize: "27pt", fontWeight: "bold" }}>ZAPTAS TECHNOLOGIES PVT LTD.</h3>
        </div>
      </div>

      {/* Content Page */}
      <div className="pdf-page">
        <div className="logo-container">
          <img src="/zimage1.png" alt="Logo" className="logo" />
        </div>

        {intro.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            {b.paragraphs.map((p, i) => <p key={i} style={{ margin: "1.1em 0", textAlign: "justify" }}>{p}</p>)}
          </div>
        ))}

        {services.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            <p style={{ textAlign: "justify" }}>{b.intro}</p>
            <ul className="pdf">{b.items.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
        ))}

        {/* ── Development: copy-paste text matches PDF body style exactly ── */}
        {development.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            <p style={{ textAlign: "justify" }}>{b.intro}</p>
            <ul className="pdf">{b.steps.map((s, i) => <li key={i}>{s}</li>)}</ul>

            {/* copy-paste text: same font-size (11pt), same color (#111827), same line-height (1.48) as body */}
            {b.copyPasteText && (
              <p style={{
                margin: "1.1em 0",
                textAlign: "justify",
                whiteSpace: "pre-wrap",
                fontSize: "11pt",
                color: "#111827",
                lineHeight: "1.48",
                fontFamily: "inherit"
              }}>
                {b.copyPasteText}
              </p>
            )}

            {/* ── CHANGE 1: image centered + width & height control ── */}
            {b.sectionImage && (
              <div style={{ textAlign: "center", margin: "1em 0" }}>
                <img
                  src={b.sectionImage}
                  alt="Section"
                  style={{
                    display: "block",
                    margin: "0 auto",
                    width: `${b.sectionImageWidth || 100}%`,
                    height: b.sectionImageHeight && b.sectionImageHeight > 0
                      ? `${b.sectionImageHeight}px`
                      : "auto",
                    borderRadius: "4px"
                  }}
                />
              </div>
            )}
          </div>
        ))}

        {customerBackground.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            {b.paragraphs.map((p, i) => <p key={i} style={{ margin: "1.1em 0", textAlign: "justify" }}>{p}</p>)}
          </div>
        ))}

        {scope.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            <div
              className="pdf-scope-intro"
              style={{ marginBottom: "1.4em", textAlign: "justify" }}
              dangerouslySetInnerHTML={{ __html: b.intro || "" }}
            />
            {b.sections.map((sec, i) => (
              <div key={i}>
                <h3 className="pdf">{sec.title}</h3>
                <ul className="pdf-scope-list">
                  {sec.items.map((item, j) => (
                    <li key={j} dangerouslySetInnerHTML={{ __html: item || "" }} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}

        {timeline.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            <p style={{ textAlign: "justify" }}>{b.intro} <strong>{b.months}</strong>{b.postfix}</p>
          </div>
        ))}

        {costing.filter(b => b.active).map(b => {
          const sub = calculateSubtotal(b.mainItems);
          const ext = calculateExtra(b.extraItems);
          return (
            <div key={b.id}>
              <h2 className="pdf">{b.heading}</h2>
              <table className="pdf">
                <thead>
                  <tr><th>S.No</th><th>Description</th><th>Amount (INR)</th></tr>
                </thead>
                <tbody>
                  {b.mainItems.map((item, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td><td>{item.desc}</td><td style={{ textAlign: "right" }}>{formatAmount(item.amt)}</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td>{b.mainItems.length + 1}</td><td>Total</td><td style={{ textAlign: "right" }}>{formatAmount(sub)}</td>
                  </tr>
                  {b.extraItems.map((item, i) => (
                    <tr key={i}>
                      <td>{b.mainItems.length + i + 2}</td><td>{item.desc}</td><td style={{ textAlign: "right" }}>{formatAmount(item.amt)}</td>
                    </tr>
                  ))}
                  <tr className="grand-total-row">
                    <td>{b.mainItems.length + b.extraItems.length + 2}</td><td>Grand Total</td><td style={{ textAlign: "right" }}>{formatAmount(sub + ext)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}

        {terms.filter(b => b.active).map(b => (
          <div key={b.id}>
            <h2 className="pdf">{b.heading}</h2>
            <ul className="pdf" style={{ paddingLeft: "24px" }}>
              {b.items.map((t, i) => <li key={i} style={{ marginBottom: "0.7em" }}>{t}</li>)}
            </ul>
          </div>
        ))}

        <div className="pdf-footer">
          <img src="/footer.jpeg" alt="Logo" className="logo2" />
        </div>
      </div>
    </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        .proposal-app { max-width: 1440px; margin: 0 auto; padding: 24px; background: #f9fafb; font-family: system-ui, sans-serif; }
        .app-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 32px; }
        .app-header h1 { margin: 0; color: #14405C; }
        .btn-download { background: #14405C; color: white; border: none; padding: 12px 24px; border-radius: 6px; font-weight: 600; cursor: pointer; }
        .btn-download:hover { background: #0f2e45; }
        .editor-card { background: white; border-radius: 12px; box-shadow: 0 4px 16px rgba(0,0,0,0.08); padding: 32px; margin-bottom: 48px; }
        .accordion-header { display: flex; justify-content: space-between; align-items: center; background: #f8f9fa; padding: 12px 16px; margin: 8px 0; cursor: pointer; font-weight: 600; border-radius: 6px; border: 1px solid #dee2e6; user-select: none; }
        .accordion-header:hover { background: #e9ecef; }
        .accordion-content { padding: 16px; border: 1px solid #dee2e6; border-top: none; border-radius: 0 0 6px 6px; background: white; }
        .form-group { margin-bottom: 20px; }
        .form-group label { display: block; margin-bottom: 6px; font-weight: 600; color: #374151; }
        textarea, input { width: 100%; padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 1rem; }
        .array-editor-item { display: flex; gap: 12px; margin-bottom: 12px; align-items: flex-start; }
        .array-editor-item textarea { flex: 1; }
        .btn-small { padding: 6px 12px; font-size: 0.9rem; border-radius: 4px; cursor: pointer; }
        .btn-add { background: #2563eb; color: white; border: none; }
        .btn-remove { background: #dc2626; color: white; border: none; }
        .btn-duplicate { background: #7c3aed; color: white; border: none; }
        ul.pdf { list-style: none; padding-left: 0; }
        ul.pdf li { position: relative; padding-left: 25px; margin-bottom: 8px; }
        ul.pdf li::before { content: "✔"; position: absolute; left: 0; color: #28a745; font-weight: bold; }

        /* Scope rich lists – top-level keeps ✔, nested preserve original bullets/numbers */
        ul.pdf-scope-list {
          list-style: none;
          padding-left: 0;
          margin: 0.6em 0;
        }
        ul.pdf-scope-list > li {
          position: relative;
          padding-left: 25px;
          margin-bottom: 8px;
        }
        ul.pdf-scope-list > li::before {
          content: "✔";
          position: absolute;
          left: 0;
          color: #28a745;
          font-weight: bold;
        }
        .pdf-scope-list ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.3em 0;
        }
        .pdf-scope-list ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.3em 0;
        }
        .pdf-scope-list ul ul { list-style-type: circle; }
        .pdf-scope-list ul ul ul { list-style-type: square; }
        .pdf-scope-list ol ol { list-style-type: lower-alpha; }
        .pdf-scope-list ol ol ol { list-style-type: lower-roman; }
        .pdf-scope-list li li::before { content: none !important; }
        .pdf-scope-list p { margin: 0.4em 0; }
        .pdf-scope-list b, .pdf-scope-list strong { font-weight: bold; }
        .pdf-scope-list i, .pdf-scope-list em { font-style: italic; }
        .pdf-scope-list u { text-decoration: underline; }

        /* Free-form rich intro (may contain paragraphs, lists, emphasis) */
        .pdf-scope-intro p { margin: 0.6em 0; text-align: inherit; }
        .pdf-scope-intro ul {
          list-style-type: disc;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .pdf-scope-intro ol {
          list-style-type: decimal;
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .pdf-scope-intro ul ul { list-style-type: circle; }
        .pdf-scope-intro b, .pdf-scope-intro strong { font-weight: bold; }
        .pdf-scope-intro i, .pdf-scope-intro em { font-style: italic; }
        .pdf-scope-intro u { text-decoration: underline; }

        /* Rich text editor (Scope of Work paste from Word) */
        .rich-editor {
          width: 100%;
          padding: 10px 14px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 1rem;
          background: #fff;
          min-height: 60px;
          overflow-y: auto;
          line-height: 1.5;
          color: #111827;
          box-sizing: border-box;
        }
        .rich-editor:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
        }
        .rich-editor:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-editor ul, .rich-editor ol {
          padding-left: 1.5em;
          margin: 0.4em 0;
        }
        .rich-editor li { margin-bottom: 0.25em; }
        .rich-editor p { margin: 0.5em 0; }
        .rich-editor p:first-child { margin-top: 0; }
        .rich-editor p:last-child { margin-bottom: 0; }
        .rich-editor b, .rich-editor strong { font-weight: bold; }
        .rich-editor i, .rich-editor em { font-style: italic; }
        .rich-editor u { text-decoration: underline; }
        .array-editor-item .rich-editor { flex: 1; }

        .scope-section { margin-bottom: 24px; border-left: 3px solid #e5e7eb; padding-left: 16px; }
        .a4-container { background: #e5e7eb; padding: 20px; border-radius: 12px; }
        .a4-content { width: 210mm; background: white; box-shadow: 0 8px 32px rgba(0,0,0,0.1); font-size: 11pt; line-height: 1.48; color: #111827; }
        .pdf-page { padding: 20mm 13mm 35mm 13mm; min-height: 255mm; box-sizing: border-box; page-break-after: always; }
        .logo-container { display: flex; justify-content: center; margin-bottom: 10mm; }
        .logo { max-width: 250px; height: auto; }
        .logo1 { margin-bottom: 105px; margin-top: -90px; max-width: 700px; height: auto; }
        .logo2 {margin-bottom: 90px; margin-top: 150px; max-width: 700px; height: auto; }
        h2.pdf { color: #C62131 !important; font-weight: bold; }
        h1.pdf { font-size: 27pt; margin: 0; font-weight: bold; }
        h2.pdf { font-size: 18pt; margin: 1em 0 0.3em; }
        h3.pdf { font-size: 14pt; margin: 1.4em 0 0.5em; }
        ul.pdf { padding-left: 20px; margin: 0.6em 0; }
        table.pdf { width: 100%; border-collapse: collapse; margin: 1em 0; }
        table.pdf th, table.pdf td { border: 1px solid #d1d5db; padding: 8px 10px; }
        table.pdf th { background: #f3f4f6; }
        .total-row td { font-weight: bold; background: #f0f5ff; }
        .grand-total-row td { font-weight: bold; background: #e3f2fd; }
        .pdf-footer { text-align: center; margin-top: 10mm; padding-top: 2mm;  color: #6b7280; font-size: 9.5pt; }
        .toggle-btn { padding: 6px 14px; font-size: 0.9rem; border-radius: 6px; color: white; border: none; cursor: pointer; min-width: 90px; text-align: center; }
        .toggle-active { background: #16a34a; }
        .toggle-inactive { background: #dc2626; }
        .section-controls { display: flex; gap: 8px; align-items: center; }
        .history-action-btn { background: #14405C; color: #fff; border: none; padding: 6px 10px; border-radius: 6px; cursor: pointer; font-weight: 600; }
        .history-action-btn:hover { background: #0f2e45; }
        .history-edit-btn { background: #0891b2; color: #fff; border: none; padding: 6px 10px; border-radius: 6px; cursor: pointer; font-weight: 600; }
        .history-edit-btn:hover { background: #0e7490; }
        .editor-card-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
        .editor-card-header h2 { margin: 0; }
        .history-delete-btn { background: #dc2626; color: #fff; border: none; padding: 6px 10px; border-radius: 6px; cursor: pointer; font-weight: 700; }
        .history-delete-btn:hover { background: #b91c1c; }
        .modal-backdrop { position: fixed; inset: 0; background: rgba(17, 24, 39, 0.65); display: flex; align-items: center; justify-content: center; padding: 24px; z-index: 9999; }
        .modal { background: #fff; width: min(1100px, 100%); max-height: 92vh; border-radius: 12px; box-shadow: 0 24px 64px rgba(0,0,0,0.35); overflow: hidden; display: flex; flex-direction: column; }
        .modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid #e5e7eb; }
        .modal-header h3 { margin: 0; font-size: 1rem; color: #111827; }
        .modal-actions { display: flex; gap: 10px; align-items: center; }
        .modal-close { background: transparent; border: 1px solid #d1d5db; border-radius: 8px; padding: 6px 10px; cursor: pointer; font-weight: 700; }
        .modal-body { padding: 16px; overflow: auto; background: #f3f4f6; }

        .dev-copy-paste-box { position: relative; }
        .dev-copy-paste-box textarea { font-family: inherit; resize: vertical; }
        .btn-copy { position: absolute; top: 8px; right: 8px; background: #0891b2; color: white; border: none; padding: 4px 10px; border-radius: 4px; font-size: 0.8rem; cursor: pointer; }
        .btn-copy:hover { background: #0e7490; }
        .btn-copy.copied { background: #16a34a; }
        .image-upload-zone { border: 2px dashed #d1d5db; border-radius: 8px; padding: 20px; text-align: center; cursor: pointer; transition: border-color 0.2s; background: #f9fafb; }
        .image-upload-zone:hover { border-color: #6366f1; background: #eef2ff; }
        .image-upload-zone input[type="file"] { display: none; }
        .image-preview-wrapper { position: relative; display: inline-block; margin-top: 12px; }
        .image-preview-wrapper img { max-width: 100%; max-height: 240px; border-radius: 6px; border: 1px solid #e5e7eb; display: block; }
        .btn-remove-img { position: absolute; top: -8px; right: -8px; background: #dc2626; color: white; border: none; border-radius: 50%; width: 24px; height: 24px; font-size: 1rem; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; }

        /* ── image width/height slider ── */
        .img-width-slider-row { display: flex; align-items: center; gap: 12px; margin-top: 10px; }
        .img-width-slider-row label { font-weight: 600; color: #374151; white-space: nowrap; min-width: 110px; }
        .img-width-slider-row input[type="range"] { flex: 1; padding: 0; border: none; }
        .img-width-slider-row span { min-width: 52px; text-align: right; font-weight: 600; color: #14405C; }

        /* ── Paginated preview pages (exact visual match to PDF) ── */
        .preview-pages-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 8px 0 32px;
        }
        .a4-page-preview {
          width: 210mm;
          max-width: 100%;
          background: #ffffff;
          box-shadow: 0 8px 32px rgba(0,0,0,0.12);
          border-radius: 2px;
          overflow: hidden;
          position: relative;
        }
        .a4-page-preview img {
          width: 100%;
          height: auto;
          display: block;
          vertical-align: top;
        }
        .preview-page-label {
          position: absolute;
          top: 8px;
          right: 12px;
          background: rgba(20, 64, 92, 0.85);
          color: #fff;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 4px;
          pointer-events: none;
          z-index: 1;
        }
        .preview-status {
          text-align: center;
          color: #6b7280;
          padding: 48px 16px;
          font-size: 0.95rem;
        }
        .preview-status.generating {
          color: #14405C;
          font-weight: 500;
        }

        /* Hidden capture source – kept off-screen so layout is measured correctly */
        .pdf-capture-source {
          position: absolute;
          left: -9999px;
          top: 0;
          width: 210mm;
          pointer-events: none;
          opacity: 0;
          z-index: -1;
        }
      `}</style>

      <div className="proposal-app">
        <header className="app-header">
          <h1>Proposal Builder</h1>
          <button className="btn-download" onClick={() => downloadPDF()}>Download PDF</button>
        </header>

        <div className="editor-card" ref={editorSectionRef}>
          <div className="editor-card-header">
            <h2>Edit Proposal Content</h2>
            <button
              className="btn-download"
              onClick={saveToHistory}
              disabled={savingHistory}
            >
              {savingHistory ? "Saving…" : editingHistoryId ? "Save Changes" : "Save to History"}
            </button>
          </div>

          {/* Client Name */}
          <div>
            <div
              className="accordion-header"
              onClick={() => toggleAccordion("client", "name")}
            >
              <span>Client / Company Name</span>
            </div>

            {openAccordions["client-name"] && (
              <div className="accordion-content">
                <div className="form-group">
                  <label>Client Name</label>
                  <input
                    value={clientName}
                    onChange={e => setClientName(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ── Introduction ─────────────────────────────── */}
          {introBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('intro', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(introBlocks, setIntroBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(introBlocks, setIntroBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {introBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(introBlocks, setIntroBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`intro-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(introBlocks, setIntroBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  {block.paragraphs.map((p, i) => (
                    <div className="form-group" key={i}>
                      <textarea
                        rows={4}
                        value={p}
                        onChange={e => {
                          const copy = [...block.paragraphs];
                          copy[i] = e.target.value;
                          updateArrayField(introBlocks, setIntroBlocks, idx, "paragraphs", copy);
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* ── Services ──────────────────────────────────── */}
          {servicesBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('services', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(servicesBlocks, setServicesBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(servicesBlocks, setServicesBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {servicesBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(servicesBlocks, setServicesBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`services-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(servicesBlocks, setServicesBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Intro text</label>
                    <textarea
                      rows={2}
                      value={block.intro}
                      onChange={e => updateBlock(servicesBlocks, setServicesBlocks, idx, { intro: e.target.value })}
                    />
                  </div>
                  {block.items.map((item, i) => (
                    <div key={i} className="array-editor-item">
                      <textarea
                        rows={2}
                        value={item}
                        onChange={e => {
                          const copy = [...block.items];
                          copy[i] = e.target.value;
                          updateArrayField(servicesBlocks, setServicesBlocks, idx, "items", copy);
                        }}
                      />
                      <button
                        className="btn-small btn-remove"
                        onClick={() => {
                          const copy = block.items.filter((_, j) => j !== i);
                          updateArrayField(servicesBlocks, setServicesBlocks, idx, "items", copy);
                        }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    className="btn-small btn-add"
                    onClick={() => updateArrayField(servicesBlocks, setServicesBlocks, idx, "items", [...block.items, "New service"])}
                  >+ Add Service</button>
                </div>
              )}
            </div>
          ))}

          {/* ── Development ───────────────────────────────── */}
          {developmentBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('development', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(developmentBlocks, setDevelopmentBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(developmentBlocks, setDevelopmentBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {developmentBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(developmentBlocks, setDevelopmentBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`development-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(developmentBlocks, setDevelopmentBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Intro text</label>
                    <textarea
                      rows={3}
                      value={block.intro}
                      onChange={e => updateBlock(developmentBlocks, setDevelopmentBlocks, idx, { intro: e.target.value })}
                    />
                  </div>
                  {block.steps.map((step, i) => (
                    <div key={i} className="array-editor-item">
                      <textarea
                        rows={2}
                        value={step}
                        onChange={e => {
                          const copy = [...block.steps];
                          copy[i] = e.target.value;
                          updateArrayField(developmentBlocks, setDevelopmentBlocks, idx, "steps", copy);
                        }}
                      />
                      <button
                        className="btn-small btn-remove"
                        onClick={() => {
                          const copy = block.steps.filter((_, j) => j !== i);
                          updateArrayField(developmentBlocks, setDevelopmentBlocks, idx, "steps", copy);
                        }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    className="btn-small btn-add"
                    onClick={() => updateArrayField(developmentBlocks, setDevelopmentBlocks, idx, "steps", [...block.steps, "New step"])}
                  >+ Add Step</button>

                  {/* Copy-Paste Text */}
                  <div className="form-group" style={{ marginTop: "24px" }}>
                    <label>Additional Notes / Copy-Paste Text</label>
                    <div className="dev-copy-paste-box" style={{ position: "relative" }}>
                      <textarea
                        rows={5}
                        placeholder="Paste or type any additional notes, requirements, or content here..."
                        value={block.copyPasteText || ""}
                        onChange={e => updateBlock(developmentBlocks, setDevelopmentBlocks, idx, { copyPasteText: e.target.value })}
                        style={{ paddingRight: "80px" }}
                      />
                      <CopyButton text={block.copyPasteText || ""} />
                    </div>
                  </div>

                  {/* Image Upload + Width & Height Sliders */}
                  <div className="form-group" style={{ marginTop: "8px" }}>
                    <label>Section Image</label>
                    {!block.sectionImage ? (
                      <label className="image-upload-zone">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) handleDevImageUpload(idx, file);
                            e.target.value = "";
                          }}
                        />
                        <div style={{ pointerEvents: "none" }}>
                          <div style={{ fontSize: "2rem", marginBottom: "8px" }}>🖼️</div>
                          <div style={{ fontWeight: 600, color: "#374151" }}>Click to upload an image</div>
                          <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "4px" }}>PNG, JPG, GIF, WEBP supported</div>
                        </div>
                      </label>
                    ) : (
                      <div>
                        <div className="image-preview-wrapper">
                          <img src={block.sectionImage} alt="Section preview" />
                          <button
                            className="btn-remove-img"
                            title="Remove image"
                            onClick={() => removeDevImage(idx)}
                          >×</button>
                        </div>

                        {/* ── CHANGE 2: Image Width slider ── */}
                        <div className="img-width-slider-row">
                          <label>Image Width:</label>
                          <input
                            type="range"
                            min={10}
                            max={100}
                            step={1}
                            value={block.sectionImageWidth || 100}
                            onChange={e =>
                              updateBlock(developmentBlocks, setDevelopmentBlocks, idx, {
                                sectionImageWidth: Number(e.target.value)
                              })
                            }
                          />
                          <span>{block.sectionImageWidth || 100}%</span>
                        </div>

                        {/* ── CHANGE 2: Image Height slider (0 = auto) ── */}
                        <div className="img-width-slider-row">
                          <label>Image Height:</label>
                          <input
                            type="range"
                            min={0}
                            max={800}
                            step={10}
                            value={block.sectionImageHeight || 0}
                            onChange={e =>
                              updateBlock(developmentBlocks, setDevelopmentBlocks, idx, {
                                sectionImageHeight: Number(e.target.value)
                              })
                            }
                          />
                          <span>
                            {block.sectionImageHeight && block.sectionImageHeight > 0
                              ? `${block.sectionImageHeight}px`
                              : "Auto"}
                          </span>
                        </div>

                        <div style={{ marginTop: "8px", fontSize: "0.85rem", color: "#6b7280" }}>
                          {block.sectionImageName}
                          <label style={{ marginLeft: "12px", color: "#2563eb", cursor: "pointer", textDecoration: "underline" }}>
                            Change
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={e => {
                                const file = e.target.files?.[0];
                                if (file) handleDevImageUpload(idx, file);
                                e.target.value = "";
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* ── Customer Background ───────────────────────── */}
          {customerBackgroundBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('customerBackground', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(customerBackgroundBlocks, setCustomerBackgroundBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(customerBackgroundBlocks, setCustomerBackgroundBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {customerBackgroundBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(customerBackgroundBlocks, setCustomerBackgroundBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`customerBackground-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(customerBackgroundBlocks, setCustomerBackgroundBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  {block.paragraphs.map((p, i) => (
                    <div className="form-group" key={i}>
                      <textarea
                        rows={4}
                        value={p}
                        onChange={e => {
                          const copy = [...block.paragraphs];
                          copy[i] = e.target.value;
                          updateArrayField(customerBackgroundBlocks, setCustomerBackgroundBlocks, idx, "paragraphs", copy);
                        }}
                      />
                    </div>
                  ))}
                  <button
                    className="btn-small btn-add"
                    onClick={() => updateArrayField(customerBackgroundBlocks, setCustomerBackgroundBlocks, idx, "paragraphs", [...block.paragraphs, "New paragraph"])}
                  >+ Add Paragraph</button>
                </div>
              )}
            </div>
          ))}

          {/* ── Scope ─────────────────────────────────────── */}
          {scopeBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('scope', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(scopeBlocks, setScopeBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(scopeBlocks, setScopeBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {scopeBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(scopeBlocks, setScopeBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`scope-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(scopeBlocks, setScopeBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Intro text <span style={{ fontWeight: 400, color: "#6b7280", fontSize: "0.85rem" }}>(paste from Word preserves formatting)</span></label>
                    <RichTextEditor
                      value={block.intro}
                      onChange={val => updateBlock(scopeBlocks, setScopeBlocks, idx, { intro: val })}
                      rows={3}
                      placeholder="Paste or type intro text… (Word formatting is preserved)"
                    />
                  </div>

                  {block.sections.map((sec, sIdx) => (
                    <div key={sIdx} className="scope-section">
                      <input
                        value={sec.title}
                        onChange={e => {
                          const copy = [...block.sections];
                          copy[sIdx] = { ...copy[sIdx], title: e.target.value };
                          updateArrayField(scopeBlocks, setScopeBlocks, idx, "sections", copy);
                        }}
                      />
                      {sec.items.map((item, itemIdx) => (
                        <div key={itemIdx} className="array-editor-item">
                          <RichTextEditor
                            value={item}
                            onChange={val => {
                              const copy = [...block.sections];
                              copy[sIdx].items = [...copy[sIdx].items];
                              copy[sIdx].items[itemIdx] = val;
                              updateArrayField(scopeBlocks, setScopeBlocks, idx, "sections", copy);
                            }}
                            rows={2}
                            placeholder="Paste feature / list item (bullets, numbers, bold, italic preserved)"
                          />
                          <button
                            className="btn-small btn-remove"
                            onClick={() => {
                              const copy = [...block.sections];
                              copy[sIdx].items = copy[sIdx].items.filter((_, j) => j !== itemIdx);
                              updateArrayField(scopeBlocks, setScopeBlocks, idx, "sections", copy);
                            }}
                          >×</button>
                        </div>
                      ))}
                      <button
                        className="btn-small btn-add"
                        onClick={() => {
                          const copy = [...block.sections];
                          copy[sIdx].items = [...copy[sIdx].items, "New feature"];
                          updateArrayField(scopeBlocks, setScopeBlocks, idx, "sections", copy);
                        }}
                      >+ Add Feature</button>
                    </div>
                  ))}

                  <button
                    className="btn-small btn-add"
                    onClick={() => updateArrayField(scopeBlocks, setScopeBlocks, idx, "sections", [...block.sections, { title: "New Module", items: ["Feature"] }])}
                  >+ Add Module</button>
                </div>
              )}
            </div>
          ))}

          {/* ── Timeline ──────────────────────────────────── */}
          {timelineBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('timeline', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(timelineBlocks, setTimelineBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(timelineBlocks, setTimelineBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {timelineBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(timelineBlocks, setTimelineBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`timeline-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(timelineBlocks, setTimelineBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Text before</label>
                    <textarea
                      rows={2}
                      value={block.intro}
                      onChange={e => updateBlock(timelineBlocks, setTimelineBlocks, idx, { intro: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Months</label>
                    <input
                      value={block.months}
                      onChange={e => updateBlock(timelineBlocks, setTimelineBlocks, idx, { months: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Text after</label>
                    <textarea
                      rows={2}
                      value={block.postfix}
                      onChange={e => updateBlock(timelineBlocks, setTimelineBlocks, idx, { postfix: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* ── Costing ───────────────────────────────────── */}
          {costingBlocks.map((block, idx) => {
            const subtotal = calculateSubtotal(block.mainItems);
            const extraTotal = calculateExtra(block.extraItems);
            const grandTotal = subtotal + extraTotal;

            return (
              <div key={block.id}>
                <div
                  className="accordion-header"
                  onClick={() => toggleAccordion('costing', block.id)}
                >
                  <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                  <div className="section-controls">
                    <button
                      className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                      onClick={e => { e.stopPropagation(); updateBlock(costingBlocks, setCostingBlocks, idx, { active: !block.active }); }}
                    >
                      {block.active ? "Active" : "Inactive"}
                    </button>
                    <button
                      className="btn-small btn-duplicate"
                      onClick={e => { e.stopPropagation(); duplicateSection(costingBlocks, setCostingBlocks, idx); }}
                    >
                      Duplicate
                    </button>
                    {costingBlocks.length > 1 && (
                      <button
                        className="btn-small btn-remove"
                        onClick={e => { e.stopPropagation(); removeSection(costingBlocks, setCostingBlocks, idx); }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {openAccordions[`costing-${block.id}`] && block.active && (
                  <div className="accordion-content">
                    <div className="form-group">
                      <label>Heading</label>
                      <input
                        value={block.heading}
                        onChange={e => updateBlock(costingBlocks, setCostingBlocks, idx, { heading: e.target.value })}
                      />
                    </div>

                    {block.mainItems.map((item, i) => (
                      <div key={i} className="array-editor-item">
                        <input style={{ width: "60px" }} value={i + 1} readOnly />
                        <input
                          style={{ flex: 3 }}
                          value={item.desc}
                          onChange={e => {
                            const copy = [...block.mainItems];
                            copy[i] = { ...copy[i], desc: e.target.value };
                            updateArrayField(costingBlocks, setCostingBlocks, idx, "mainItems", copy);
                          }}
                        />
                        <input
                          style={{ flex: 1, textAlign: "right" }}
                          value={item.amt}
                          onChange={e => {
                            const copy = [...block.mainItems];
                            copy[i] = { ...copy[i], amt: e.target.value };
                            updateArrayField(costingBlocks, setCostingBlocks, idx, "mainItems", copy);
                          }}
                        />
                        <button
                          className="btn-small btn-remove"
                          onClick={() => {
                            const copy = block.mainItems.filter((_, j) => j !== i);
                            updateArrayField(costingBlocks, setCostingBlocks, idx, "mainItems", copy);
                          }}
                        >×</button>
                      </div>
                    ))}

                    <button
                      className="btn-small btn-add"
                      onClick={() => updateArrayField(costingBlocks, setCostingBlocks, idx, "mainItems", [...block.mainItems, { desc: "New item", amt: "0.00" }])}
                    >+ Add Main Item</button>

                    <div className="array-editor-item" style={{ margin: "24px 0", fontWeight: "bold" }}>
                      <input style={{ width: "60px" }} value={block.mainItems.length + 1} readOnly />
                      <input style={{ flex: 3 }} value="Total" readOnly />
                      <input style={{ flex: 1, textAlign: "right" }} value={formatAmount(subtotal)} readOnly />
                    </div>

                    {block.extraItems.map((item, i) => (
                      <div key={i} className="array-editor-item">
                        <input style={{ width: "60px" }} value={block.mainItems.length + i + 2} readOnly />
                        <input
                          style={{ flex: 3 }}
                          value={item.desc}
                          onChange={e => {
                            const copy = [...block.extraItems];
                            copy[i] = { ...copy[i], desc: e.target.value };
                            updateArrayField(costingBlocks, setCostingBlocks, idx, "extraItems", copy);
                          }}
                        />
                        <input
                          style={{ flex: 1, textAlign: "right" }}
                          value={item.amt}
                          onChange={e => {
                            const copy = [...block.extraItems];
                            copy[i] = { ...copy[i], amt: e.target.value };
                            updateArrayField(costingBlocks, setCostingBlocks, idx, "extraItems", copy);
                          }}
                        />
                        <button
                          className="btn-small btn-remove"
                          onClick={() => {
                            const copy = block.extraItems.filter((_, j) => j !== i);
                            updateArrayField(costingBlocks, setCostingBlocks, idx, "extraItems", copy);
                          }}
                        >×</button>
                      </div>
                    ))}

                    <button
                      className="btn-small btn-add"
                      onClick={() => updateArrayField(costingBlocks, setCostingBlocks, idx, "extraItems", [...block.extraItems, { desc: "New extra", amt: "0.00" }])}
                    >+ Add Extra</button>

                    <div className="array-editor-item" style={{ margin: "24px 0", fontWeight: "bold" }}>
                      <input style={{ width: "60px" }} value={block.mainItems.length + block.extraItems.length + 2} readOnly />
                      <input style={{ flex: 3 }} value="Grand Total" readOnly />
                      <input style={{ flex: 1, textAlign: "right" }} value={formatAmount(grandTotal)} readOnly />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* ── Terms ─────────────────────────────────────── */}
          {termsBlocks.map((block, idx) => (
            <div key={block.id}>
              <div
                className="accordion-header"
                onClick={() => toggleAccordion('terms', block.id)}
              >
                <span>{block.heading} {idx > 0 ? '(copy)' : ''}</span>
                <div className="section-controls">
                  <button
                    className={`toggle-btn ${block.active ? 'toggle-active' : 'toggle-inactive'}`}
                    onClick={e => { e.stopPropagation(); updateBlock(termsBlocks, setTermsBlocks, idx, { active: !block.active }); }}
                  >
                    {block.active ? "Active" : "Inactive"}
                  </button>
                  <button
                    className="btn-small btn-duplicate"
                    onClick={e => { e.stopPropagation(); duplicateSection(termsBlocks, setTermsBlocks, idx); }}
                  >
                    Duplicate
                  </button>
                  {termsBlocks.length > 1 && (
                    <button
                      className="btn-small btn-remove"
                      onClick={e => { e.stopPropagation(); removeSection(termsBlocks, setTermsBlocks, idx); }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>

              {openAccordions[`terms-${block.id}`] && block.active && (
                <div className="accordion-content">
                  <div className="form-group">
                    <label>Heading</label>
                    <input
                      value={block.heading}
                      onChange={e => updateBlock(termsBlocks, setTermsBlocks, idx, { heading: e.target.value })}
                    />
                  </div>
                  {block.items.map((term, i) => (
                    <div key={i} className="array-editor-item">
                      <textarea
                        rows={2}
                        value={term}
                        onChange={e => {
                          const copy = [...block.items];
                          copy[i] = e.target.value;
                          updateArrayField(termsBlocks, setTermsBlocks, idx, "items", copy);
                        }}
                      />
                      <button
                        className="btn-small btn-remove"
                        onClick={() => {
                          const copy = block.items.filter((_, j) => j !== i);
                          updateArrayField(termsBlocks, setTermsBlocks, idx, "items", copy);
                        }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    className="btn-small btn-add"
                    onClick={() => updateArrayField(termsBlocks, setTermsBlocks, idx, "items", [...block.items, "New condition"])}
                  >+ Add Term</button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Download history */}
        <div className="editor-card">
          <h2>Proposal Download History</h2>
          {loadingHistory && <p>Loading history...</p>}
          {historyError && <p style={{ color: "red" }}>{historyError}</p>}
          {!loadingHistory && !historyError && downloadHistory.length === 0 && (
            <p style={{ color: "#6b7280" }}>No downloads recorded yet.</p>
          )}
          {downloadHistory.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="pdf" style={{ width: "100%", fontSize: "0.9rem" }}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Client Name</th>
                    <th>Downloaded At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {downloadHistory.map((row, index) => (
                    <tr key={row._id || index}>
                      <td>{index + 1}</td>
                      <td>{row.clientName}</td>
                      <td>
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <button
                            className="history-action-btn"
                            onClick={() =>
                              setHistoryPreview({
                                open: true,
                                id: row._id || "",
                                clientName: row.clientName || "",
                                proposal: row.meta?.proposal || null,
                              })
                            }
                          >
                            View
                          </button>
                          <button
                            className="history-edit-btn"
                            onClick={() => handleEditFromHistory(row)}
                          >
                            Edit
                          </button>
                          <button
                            className="history-delete-btn"
                            onClick={async () => {
                              if (!row._id) return;
                              const ok = window.confirm("Delete this history record?");
                              if (!ok) return;
                              try {
                                await Services.ProposalAndRateCard.deleteProposalDownload(row._id);
                                if (historyPreview.open && historyPreview.id === row._id) {
                                  setHistoryPreview({ open: false, id: "", clientName: "", proposal: null });
                                }
                                if (editingHistoryId === row._id) {
                                  setEditingHistoryId(null);
                                }
                                loadHistory();
                              } catch (e) {
                                console.error("Failed to delete proposal download record", e);
                                alert("Delete failed.");
                              }
                            }}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* History PDF Preview Modal */}
        {historyPreview.open && (
          <div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            onClick={() => setHistoryPreview({ open: false, id: "", clientName: "", proposal: null })}
          >
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>PDF Preview — {historyPreview.clientName || "Client"}</h3>
                <div className="modal-actions">
                  <button className="btn-download" onClick={downloadFromHistoryAndReplace}>
                    Download PDF
                  </button>
                  <button
                    className="modal-close"
                    onClick={() => setHistoryPreview({ open: false, id: "", clientName: "", proposal: null })}
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="modal-body">
                {/* Hidden source for capture */}
                <div className="pdf-capture-source">
                  {renderPdfContent(historyPdfRef, historyPreview.clientName, historyPreview.proposal)}
                </div>

                <div className="a4-container">
                  {generatingHistoryPreview ? (
                    <div className="preview-status generating">Generating page previews…</div>
                  ) : historyPreviewPages.length === 0 ? (
                    <div className="preview-status">No preview available.</div>
                  ) : (
                    <div className="preview-pages-wrapper">
                      {historyPreviewPages.map((src, i) => (
                        <div key={i} className="a4-page-preview">
                          <span className="preview-page-label">Page {i + 1}</span>
                          <img src={src} alt={`Page ${i + 1}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PDF Preview – exact same pages as the downloaded PDF */}
        <section>
          <h2>PDF Preview (A4)</h2>
          <div className="a4-container">
            {/* Hidden source used by both live preview & Download PDF */}
            <div className="pdf-capture-source">
              {renderPdfContent(pdfRef, clientName)}
            </div>

            {generatingPreview ? (
              <div className="preview-status generating">Generating page previews…</div>
            ) : previewPages.length === 0 ? (
              <div className="preview-status">No preview available.</div>
            ) : (
              <div className="preview-pages-wrapper">
                {previewPages.map((src, i) => (
                  <div key={i} className="a4-page-preview">
                    <span className="preview-page-label">Page {i + 1}</span>
                    <img src={src} alt={`Page ${i + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

// ── Copy button with "Copied!" feedback ─────────────────────────────
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      className={`btn-copy${copied ? " copied" : ""}`}
      onClick={handleCopy}
      title="Copy to clipboard"
    >
      {copied ? "✓ Copied!" : "Copy"}
    </button>
  );
};

export default Proposal;